import React, { useState, useEffect } from 'react';
import { Layout, Button, Switch, notification, Upload, Modal, Table, Popconfirm, Form, Input, Row, Col, Divider } from 'antd';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import DownloadReportButton from '../Molecules/DownloadReport';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import CreateCampaignModal from '../Molecules/CreateCampaignModal';

const baseURL = process.env.REACT_APP_API_URL;
const { Header } = Layout;

const AdminPage = () => {
    const baseURL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    const [switchState, setSwitchState] = useState([false, false, false]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [eltData, setEltData] = useState<{ eltEmail1: string; eltEmailKey: string; }[]>([]);
    const [editingKey, setEditingKey] = useState('');
    const [isSuperAdminModal, setIsSuperAdminModal] = useState(false);
    const [superAdminData, setSuperAdminData] = useState([]);
    const [form] = Form.useForm();
    const { instance, accounts } = useMsal();
    const [isNewCampaignModalVisible, setIsNewCampaignModalVisible] = useState(false);
    const [visible, setVisible] = useState(false);
    const [empData, setEmpData] = useState<any>(null);
    const [isNewCampaign, setIsNewCampaign] = useState(false);


    const showNotification = (type: 'success' | 'error', message: string, description: string) => {
        notification[type]({
            message,
            description,
        });
    };

    const fetchToken = async () => {
          const res = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
          });
          return res.idToken;
        };
      
        useEffect(() => {
          fetchToken();
        }, []);
    

    const handleCreateNewCampaign = () => {
        setIsNewCampaign(true);
        setEmpData(null); // Clear existing data
        setVisible(true); // Show the modal
    };


    const handleShowCurrentCampaign = async () => {
        setIsNewCampaign(false);
        const token = await fetchToken();
        try {
            // Fetch the active campaign from the API
            const response = await axios.get(`${baseURL}/api/nominations/campaigninfo`, {
                headers: {  Authorization: `Bearer ${token}` }
            });
            if (response.data) {
                setEmpData(response.data); // Set the current campaign data
            }
        } catch (error) {
            console.error("Failed to fetch current campaign:", error);
        }
        setVisible(true); // Show the modal
    };

    const handleCancel = () => {
        setVisible(false); // Hide the modal
    };

    const handleEditCurrentCampaignClick = () => {
        navigate('/admin/view/individual');
    }

    const handleUpload = async (file: any) => {
        const token = await fetchToken();
        try {
            const formData = new FormData();
            formData.append('file', file);

            await axios.post(`${baseURL}/api/nominations/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                },
            });

            showNotification('success', 'Success', 'Nominations uploaded successfully.');
        } catch (error) {
            showNotification('error', 'Error', 'Failed to upload nominations.');
        }
    };

    const uploadProps = {
        name: 'file',
        customRequest: async (options: any) => {
            const { file, onSuccess, onError } = options;
            try {
                await handleUpload(file);
                onSuccess("Ok");
            } catch (error) {
                onError(error);
            }
        },
    };

    const fetchEltData = async () => {
        try {
            const token = await fetchToken();
            const response = await axios.get(`${baseURL}/api/nominations/elt/list`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            setEltData(response.data.responseData);
        }
        catch (error) {
            showNotification('error', 'Error', 'Failed to fetch ELT data.');
        }
    };

    const fetchSuperAdminData = async () => {
        try {
            const token = await fetchToken();
            const response = await axios.get(`${baseURL}/api/nominations/superadmin/list`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            setSuperAdminData(response.data.responseData);
        }
        catch (error) {
            showNotification('error', 'Error', 'Failed to fetch Super Admin data.');
        }
    };


    const handleEltClick = async () => {
        await fetchEltData();
        setIsModalVisible(true);
    }

    const handleModalClose = () => {
        setIsModalVisible(false);
    }

    const handleReportClick = () => {
        notification.success({
            message: 'Report',
            description: 'Report sent successfully.',
        });
    }

    const isEditing = (record: any) => record.eltEmailKey === editingKey;

    const edit = (record: any) => {
        form.setFieldsValue({
            eltEmail1: '',
            eltEmailKey: '',
            ...record,
        });
        setEditingKey(record.eltEmailKey);
    };

    const handleClickCampaignModal = () => {
        setIsNewCampaignModalVisible(true);
    }


    const handleCancelCampaignModal = () => {
        setVisible(false);
    }

    const cancel = () => {
        setEditingKey('');
    };

    const save = async (key: React.Key) => {
        try {
            const token = await fetchToken();
            const row = await form.validateFields();
            const newData = [...eltData];
            const index = newData.findIndex((item) => key === item.eltEmailKey);

            if (index > -1) {
                const item = newData[index];
                newData.splice(index, 1, { ...item, ...row });
                setEltData(newData);
                setEditingKey('');

                // Send updated data to the server
                
                await axios.put(`${baseURL}/api/nominations/elt/update`, newData[index], {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
            } else {
                newData.push(row);
                setEltData(newData);
                setEditingKey('');
            }
        } catch (errInfo) {
            console.error('Validate Failed:', errInfo);
        }
    };

    const deleteRecord = async (key: React.Key) => {
        try {
            const token = await fetchToken();
            const newData = eltData.filter((item) => item.eltEmailKey !== key);
            setEltData(newData);

            // Send delete request to the server
            
            await axios.delete(`${baseURL}/api/elt-delete/${key}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (error) {
            console.error('Failed to delete ELT data.', error);
        }
    };

    const handleSuperAdminModalClick = async () => {
        await fetchSuperAdminData();
        setIsSuperAdminModal(true);
    }

    const handleSuperAdminModalClose = () => {
        setIsSuperAdminModal(false);
    }

    const handleArchiveClick = () => {
        navigate('/review/final/individual')
    }

    const handleResetStackRanking = () => {
        notification.success({
            message: 'Stack Ranking',
            description: 'Stack Ranking has been reset successfully.',
        });
    }

    const columns = [
        {
            title: 'ELT Email',
            dataIndex: 'eltEmail1',
            key: 'eltEmail1',
            editable: true,
        },
        {
            title: 'ELT Email Key',
            dataIndex: 'eltEmailKey',
            key: 'eltEmailKey',
        },
        {
            title: 'Action',
            dataIndex: 'action',
            render: (_: any, record: any) => {
                const editable = isEditing(record);
                return editable ? (
                    <span>
                        <Button
                            onClick={() => save(record.eltEmailKey)}
                            style={{
                                marginRight: 8,
                            }}
                        >
                            Save
                        </Button>
                        <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                            <Button>Cancel</Button>
                        </Popconfirm>
                    </span>
                ) : (
                    <>
                        <Button
                            disabled={editingKey !== ''}
                            onClick={() => edit(record)}
                            style={{ marginRight: 8 }}
                        >
                            Edit
                        </Button>
                        <Popconfirm title="Sure to delete?" onConfirm={() => deleteRecord(record.eltEmailKey)}>
                            <Button danger>Delete</Button>
                        </Popconfirm>
                    </>
                );
            },
        },
    ];

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }
        return {
            ...col,
            onCell: (record: any) => ({
                record,
                inputType: col.dataIndex === 'eltEmail1' ? 'text' : null,
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    const EditableCell: React.FC<any> = ({
        editing,
        dataIndex,
        title,
        inputType,
        record,
        index,
        children,
        ...restProps
    }) => {
        const inputNode = <Input />;
        return (
            <td {...restProps}>
                {editing ? (
                    <Form.Item
                        name={dataIndex}
                        style={{ margin: 0 }}
                        rules={[
                            {
                                required: true,
                                message: `Please Input ${title}!`,
                            },
                        ]}
                    >
                        {inputNode}
                    </Form.Item>
                ) : (
                    children
                )}
            </td>
        );
    };


    const superAdminColumns = [
        {
            title: 'Super Admin Name',
            dataIndex: 'superAdminName',
            key: 'superAdminName',
        },
        {
            title: 'Super Admin Email',
            dataIndex: 'superAdminEmail',
            key: 'superAdminEmail',
        },
    ];

    const patchStage = async (patchData: any) => {
        try {
            const token = await fetchToken();
            await axios.patch(`${baseURL}/api/nominations/stage/1`, patchData, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (error) {
            navigate('/error');
        }
    };

    const handleSwitch = async (index: number) => {
        const newSwitchState = [false, false, false];
        newSwitchState[index] = true;
        setSwitchState(newSwitchState);

        let message = '';
        let description = '';
        let patchData: { op: string, path: string, value: string }[] = [];

        if (index === 0) {
            message = 'Enabled';
            description = 'You have successfully First Stage of the Review Process';
            patchData = [
                { op: 'replace', path: '/ManagerReview', value: 'active' },
                { op: 'replace', path: '/FirstReview', value: 'active' },
                { op: 'replace', path: '/SecondReview', value: 'active' },
                { op: 'replace', path: '/ELTGroupReview', value: 'inactive' },
                { op: 'replace', path: '/FinalReview', value: 'inactive' },
            ];
        } else if (index === 1) {
            message = 'Enabled';
            description = 'You have successfully Enabled ELT Group Review';
            patchData = [
                { op: 'replace', path: '/ManagerReview', value: 'inactive' },
                { op: 'replace', path: '/FirstReview', value: 'inactive' },
                { op: 'replace', path: '/SecondReview', value: 'inactive' },
                { op: 'replace', path: '/ELTGroupReview', value: 'active' },
                { op: 'replace', path: '/FinalReview', value: 'inactive' },
            ];
        } else if (index === 2) {
            message = 'Enabled';
            description = 'You have successfully Final Review';
            patchData = [
                { op: 'replace', path: '/ManagerReview', value: 'inactive' },
                { op: 'replace', path: '/FirstReview', value: 'inactive' },
                { op: 'replace', path: '/SecondReview', value: 'inactive' },
                { op: 'replace', path: '/ELTGroupReview', value: 'inactive' },
                { op: 'replace', path: '/FinalReview', value: 'active' },
            ];
        }

        showNotification('success', message, description);
        patchStage(patchData);
    };

    return (
        <div>
            <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                <div className="demo-logo" />
                <h1 style={{ color: 'whitesmoke', justifyItems: 'center' }}>Global Awards</h1>
                <Button type="primary" style={{ float: 'right', marginTop: '10px' }}>
                    <Link to={"/login"}>Log Out</Link>
                </Button>
            </Header>
            <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Admin Dashboard</h3>
            <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', paddingLeft: '10px', color: 'red' }}>
                R and R Program
            </h2>
            <Divider >Campaigns</Divider>

            <Row gutter={16}>
                <Col span={8}>
                    <div style={{ display: 'flex' }}>
                        <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', paddingLeft: '10px' }}>Create New Campaign: </h2>
                        <Button type="primary" onClick={handleCreateNewCampaign} style={{ marginTop: '10px', marginLeft: '10px' }}>Start</Button>
                    </div>
                </Col>

                <Col span={8}>
                    <div style={{ display: 'flex' }}>
                        <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', paddingLeft: '10px' }}>Current Campaign: </h2>
                        <Button type="primary" onClick={handleShowCurrentCampaign} style={{ marginTop: '10px', marginLeft: '10px' }}>Open</Button>
                    </div>
                </Col>


                <Col span={8}>
                    <div style={{ display: 'flex' }}>
                        <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', paddingLeft: '10px' }}>Edit Current Campaign: </h2>
                        <Button type="primary" onClick={handleEditCurrentCampaignClick} style={{ marginTop: '10px', marginLeft: '10px' }}>Open</Button>
                    </div>
                </Col>
            </Row>

            <Divider >Manual Reminders</Divider>


            <Row gutter={16}>
                <Col span={12}>
                    <div style={{ display: 'flex' }}>
                        <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', paddingLeft: '10px' }}>Manual Reminders - People's Manager</h2>
                        <Button type="primary" onClick={handleArchiveClick} style={{ marginTop: '10px', marginLeft: '10px' }}>Send</Button>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ display: 'flex' }}>
                        <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', paddingLeft: '10px' }}>Manual Reminders - First Level Manager</h2>
                        <Button type="primary" onClick={handleArchiveClick} style={{ marginTop: '10px', marginLeft: '10px' }}>Send</Button>
                    </div>
                </Col>
            </Row>

            <Divider >Archives</Divider>

            <Row gutter={16}>
                <Col span={12}>
                    <div style={{ display: 'flex' }}>
                        <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', paddingLeft: '10px' }}>Archives: </h2>
                        <Button type="primary" onClick={handleArchiveClick} style={{ marginTop: '10px', marginLeft: '10px' }}>Open</Button>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ display: 'flex' }}>
                        <DownloadReportButton />
                    </div>
                </Col>
            </Row>

            <Divider >Reports</Divider>

            <Row gutter={16}>
                <Col span={12}>
                    <div style={{ display: 'flex' }}>
                        <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', paddingLeft: '10px' }}>Detailed Report: </h2>
                        <Input style={{ marginTop: '10px', marginLeft: '10px', width: '200px' }} placeholder="Enter the email ID" />
                        <Button type="primary" onClick={handleReportClick} style={{ marginTop: '10px', marginLeft: '10px' }}>Send</Button>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ display: 'flex' }}>
                        <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', paddingLeft: '10px' }}>Summary Report: </h2>
                        <Input style={{ marginTop: '10px', marginLeft: '10px', width: '200px' }} placeholder="Enter the email ID" />
                        <Button type="primary" onClick={handleReportClick} style={{ marginTop: '10px', marginLeft: '10px' }}>Send</Button>
                    </div>
                </Col>
            </Row>

            <Divider>Operations</Divider>

            <Row gutter={16}>
                <Col span={12}>
                    <div style={{ display: 'flex', marginLeft: '10px' }}>
                        <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left' }}>Upload Nominations: </h2>
                        <Upload {...uploadProps}>
                            <Button style={{ marginTop: '10px', marginLeft: '10px' }} icon={<UploadOutlined />}>
                                Click to Upload
                            </Button>
                        </Upload>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ display: 'flex' }}>
                        <h2 style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', paddingLeft: '10px' }}>Reset Stack Ranking: </h2>
                        <Button type="primary" onClick={handleResetStackRanking} style={{ marginTop: '10px', marginLeft: '10px' }}>Start</Button>
                    </div>
                </Col>
            </Row>

            <Divider>ELT and Super Admin Data</Divider>


            <Row gutter={16}>
                <Col span={12}>
                    <Button style={{ marginTop: '10px', marginLeft: '10px' }} onClick={handleEltClick}>
                        List of ELT's
                    </Button>
                </Col>
                <Col span={12}>
                    <Button style={{ marginTop: '10px', marginLeft: '10px' }} onClick={handleSuperAdminModalClick}>
                        List of Super Admins
                    </Button>
                </Col>
            </Row>

            <Modal
                title="List of ELTs"
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={[
                    <Button key="close" onClick={handleModalClose}>
                        Close
                    </Button>,
                ]}
            >
                <Form form={form} component={false}>
                    <Table
                        components={{
                            body: {
                                cell: EditableCell,
                            },
                        }}
                        bordered
                        dataSource={eltData}
                        columns={mergedColumns}
                        rowClassName="editable-row"
                        pagination={false}
                        rowKey="eltEmailKey"
                    />
                </Form>
            </Modal>

            <Modal
                title="List of Super Admins"
                open={isSuperAdminModal}
                onCancel={handleSuperAdminModalClose}
                footer={null}
            >
                <Table columns={superAdminColumns} dataSource={superAdminData} rowKey="eltEmailKey" />
            </Modal>

            <CreateCampaignModal visible={visible} onCancel={handleCancelCampaignModal} empData={empData} isNewCampaign={isNewCampaign} />
            {/* <AdminNomineeModal
                visible={visible}
                onCancel={handleCancel}
                empData={empData}
            /> */}

            <div className='toggleSwitches' style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', marginLeft: '10px' }}>
                <h3>Stage 1</h3>
                <Switch checked={switchState[0]} onChange={() => handleSwitch(0)} />
                <h3>Stack Ranking</h3>
                <Switch checked={switchState[1]} onChange={() => handleSwitch(1)} />
                <h3>Final Review</h3>
                <Switch checked={switchState[2]} onChange={() => handleSwitch(2)} />
            </div>
            <h1 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Summary</h1>
        </div >
    );
}

export default AdminPage;
